/**
 * TarpFlowRunner (GUPM-173) — executa o fluxo ramificado do TARP na UI.
 * Fiel ao `renderFlowSteps` do protótipo v5.10: TODOS os passos ficam visíveis
 * em ordem canônica (etapas 0..N), cada um com estado atual/seguida/pulada.
 * Passos ainda não alcançados aparecem esmaecidos e DESABILITADOS (não
 * escondidos) — quem preenche enxerga o protocolo inteiro. Decisões têm
 * SIM (verde) / NÃO (vermelho) abaixo e são re-respondíveis (refazem o caminho).
 * Identidade visual alinhada ao form de Troca de Plantão. Componente puro.
 */
import { type Ref, useEffect, useRef, useState } from 'react';
import {
  answerAt,
  beginFlow,
  currentNode,
  decideAt,
  type Flow,
  type FlowNode,
  type FlowState,
  isActionDone,
  isTerminal,
  nodeExecState,
  type NodeExecState,
  orderedNodeIds,
  outcome,
  type Outcome,
  toggleAction,
} from '@/domain/tarp/flow';

/**
 * Usa o estado salvo (rascunho) só se for ÍNTEGRO: caminho não-vazio e todos os
 * nós existentes neste fluxo. Um `flow_state` corrompido/legado/parcial degrada
 * para `beginFlow` em vez de quebrar o render (`currentNode` lançaria).
 */
function hydrateFlowState(flow: Flow, initial: FlowState | undefined): FlowState {
  // Defensivo no I/O: flow_state é jsonb sem CHECK e gravável via API crua, então
  // pode chegar malformado ({}, path não-array, ids não-string). Valida a forma
  // ANTES de usar — senão o `.length`/`.every` estoura e derruba o render do resume.
  if (
    initial &&
    Array.isArray(initial.path) &&
    initial.path.length > 0 &&
    initial.path.every((id) => typeof id === 'string' && flow.nodes[id])
  ) {
    return initial;
  }
  return beginFlow(flow);
}

const BADGE: Record<FlowNode['tipo'], { label: string; cls: string }> = {
  inicio: { label: 'INÍCIO', cls: 'text-sky-300 border-sky-300/50 bg-sky-300/10' },
  decisao: { label: 'DECISÃO', cls: 'text-jf-yellow border-jf-yellow/50 bg-jf-yellow/10' },
  acao: { label: 'AÇÃO', cls: 'text-slate-300 border-slate-300/40 bg-slate-300/10' },
  diagnostico: { label: 'DIAGNÓSTICO', cls: 'text-sky-300 border-sky-300/50 bg-sky-300/10' },
  baixa: { label: 'BAIXA', cls: 'text-lime-300 border-lime-300/50 bg-lime-300/10' },
  paebm: { label: 'PAEBM', cls: 'text-red-400 border-red-400/50 bg-red-400/10' },
  fim: { label: 'CONCLUSÃO', cls: 'text-level-normal border-level-normal/50 bg-level-normal/10' },
};

export function TarpFlowRunner({
  flow,
  initialState,
  onOutcomeChange,
  onStateChange,
  mode = 'edit',
}: {
  flow: Flow;
  /** Estado inicial (GUPM-178): hidrata um rascunho salvo. Default = `beginFlow(flow)`. */
  initialState?: FlowState;
  /** Reporta o desfecho do nó atual (ou null quando não é terminal). Reporta em cada mudança. */
  onOutcomeChange?: (o: Outcome | null) => void;
  /** Autosave (GUPM-178): reporta o FlowState a cada mudança do checklist (não no mount). */
  onStateChange?: (s: FlowState) => void;
  /**
   * Modo de exibição (GUPM-190 T8):
   * - `edit` (default): interativo, todos os passos visíveis.
   * - `locked`: todos os passos visíveis mas DESABILITADOS (ex.: antes de escolher
   *   a barragem) — lê como "esperando começar", não como revisão.
   * - `review`: só o caminho percorrido, somente leitura (baixa/revisão).
   */
  mode?: 'edit' | 'locked' | 'review';
}) {
  const [state, setState] = useState(() => hydrateFlowState(flow, initialState));
  const order = orderedNodeIds(flow);
  const reviewing = mode === 'review';
  const interactive = mode === 'edit';
  // Revisão (baixa): mostra só o caminho percorrido — os passos não-aplicáveis
  // viram ruído. Em edição/travado, todos ficam visíveis (GUPM-173).
  const visibleOrder = reviewing ? order.filter((id) => state.path.includes(id)) : order;
  const current = currentNode(flow, state);
  const currentId = current?.id;
  const lastReported = useRef<Outcome | null | undefined>(undefined);

  // Refs p/ chamar os callbacks mais recentes sem colocá-los nas deps do efeito
  // (evita re-disparo por identidade de função inline do pai).
  const onStateChangeRef = useRef(onStateChange);
  onStateChangeRef.current = onStateChange;
  const mounted = useRef(false);
  // Auto-scroll (GUPM-190 T8): traz a etapa atual à vista quando ela muda.
  const currentCardRef = useRef<HTMLDivElement | null>(null);
  const scrolledOnce = useRef(false);

  useEffect(() => {
    const o = outcome(current);
    if (lastReported.current !== o) {
      lastReported.current = o;
      onOutcomeChange?.(o);
    }
  }, [current, onOutcomeChange]);

  // Autosave: reporta SÓ quando `state` muda de verdade (ação do usuário). Pula a
  // hidratação no mount — senão o resume re-salvaria o estado inicial por cima do
  // progresso persistido (regressão do rascunho).
  useEffect(() => {
    if (!mounted.current) {
      mounted.current = true;
      return;
    }
    onStateChangeRef.current?.(state);
  }, [state]);

  // Traz a etapa atual à vista quando ela muda (avança ao responder ou retrocede
  // ao desmarcar). Só em edição; pula o mount (não salta ao abrir o modal).
  useEffect(() => {
    if (!interactive) return;
    if (!scrolledOnce.current) {
      scrolledOnce.current = true;
      return;
    }
    currentCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [currentId, interactive]);

  const totalEtapas = order.length - 1;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-jf-dim">
        <span className="h-2 w-2 rounded-full bg-jf-yellow" />
        {reviewing ? 'Protocolo percorrido (somente leitura)' : `Fluxo de ações — etapas 0 a ${totalEtapas}`}
      </div>
      {interactive && (
        <>
          <p className="text-[11px] leading-relaxed text-jf-muted">
            Responda as decisões (Sim/Não) e marque as ações.
          </p>
          <Legend />
        </>
      )}
      {mode === 'locked' && (
        <p className="text-[11px] leading-relaxed text-jf-muted">
          Escolha a barragem acima para liberar o protocolo.
        </p>
      )}

      {visibleOrder.map((id) => {
        const node = flow.nodes[id];
        if (!node) return null;
        return (
          <FlowNodeCard
            key={id}
            node={node}
            label={flow.labels?.[id] ?? id}
            estado={nodeExecState(flow, state, id)}
            answer={answerAt(flow, state, id)}
            actionDone={isActionDone(state, id)}
            interactive={interactive}
            cardRef={currentId === id ? currentCardRef : undefined}
            onDecide={(choice) => setState((s) => decideAt(flow, s, id, choice))}
            onToggleAction={() => setState((s) => toggleAction(flow, s, id))}
          />
        );
      })}
    </div>
  );
}

function Legend() {
  const item = (bg: string, txt: string) => (
    <span className="flex items-center gap-1.5">
      <span className={`h-2 w-2 rounded-sm ${bg}`} aria-hidden />
      {txt}
    </span>
  );
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[10px] text-jf-dim">
      {item('bg-jf-yellow', 'Etapa atual')}
      {item('bg-level-normal', 'Seguida')}
      {item('bg-jf-border', 'Pulada / não se aplica')}
    </div>
  );
}

function FlowNodeCard({
  node,
  label,
  estado,
  answer,
  actionDone,
  interactive,
  cardRef,
  onDecide,
  onToggleAction,
}: {
  node: FlowNode;
  label: string;
  estado: NodeExecState;
  answer: 'sim' | 'nao' | null;
  actionDone: boolean;
  interactive: boolean;
  /** Ref no cartão atual — usado pelo auto-scroll (GUPM-190 T8). */
  cardRef?: Ref<HTMLDivElement>;
  onDecide: (choice: 'sim' | 'nao') => void;
  onToggleAction: () => void;
}) {
  const atual = estado === 'atual';
  const seguida = estado === 'seguida';
  const pulada = estado === 'pulada';
  const canInteract = (atual || seguida) && interactive; // decisões re-respondíveis; ações marcáveis
  const badge = BADGE[node.tipo];

  const shell = atual
    ? 'border-jf-yellow bg-jf-yellow/[0.06]'
    : seguida
      ? 'border-level-normal/40 bg-level-normal/[0.05]'
      : 'border-jf-border bg-jf-surface2/40 opacity-50';

  return (
    <div ref={cardRef} data-node={node.id} className={`rounded-lg border p-3 ${shell}`}>
      <div className="mb-1.5 flex items-center gap-2">
        {/* O selo do tipo já identifica o INÍCIO — o rótulo '0'/'start' é redundante. */}
        {node.tipo !== 'inicio' && (
          <span className="min-w-[18px] text-[11px] font-bold tabular-nums text-jf-muted">{label}</span>
        )}
        <span className={`rounded border px-1.5 py-0.5 text-[9px] font-extrabold tracking-wider ${badge.cls}`}>
          {badge.label}
        </span>
        {pulada && (
          <span className="ml-auto text-[9px] font-bold uppercase tracking-wide text-jf-dim">
            Pulada / não se aplica
          </span>
        )}
        {seguida && <span className="ml-auto text-xs text-level-normal">✓</span>}
      </div>

      <p className="whitespace-pre-line text-[13px] leading-snug text-slate-200">{node.texto}</p>

      {node.tipo === 'decisao' && (
        <div className="mt-2.5">
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!canInteract}
              onClick={() => onDecide('sim')}
              className={`flex-1 rounded-lg border-[1.5px] border-level-normal/50 py-2 text-[13px] font-extrabold text-level-normal transition disabled:opacity-40 ${
                answer === 'sim' ? 'bg-level-normal/20' : 'bg-transparent hover:bg-level-normal/10'
              }`}
            >
              Sim{answer === 'sim' ? ' ✓' : ''}
            </button>
            <button
              type="button"
              disabled={!canInteract}
              onClick={() => onDecide('nao')}
              className={`flex-1 rounded-lg border-[1.5px] border-red-500/50 py-2 text-[13px] font-extrabold text-red-400 transition disabled:opacity-40 ${
                answer === 'nao' ? 'bg-red-500/20' : 'bg-transparent hover:bg-red-500/10'
              }`}
            >
              Não{answer === 'nao' ? ' ✓' : ''}
            </button>
          </div>
          {answer && (answer === 'sim' ? node.simNote : node.naoNote) && (
            <p className="mt-1.5 text-[11px] italic text-jf-muted">
              {answer === 'sim' ? node.simNote : node.naoNote}
            </p>
          )}
          {seguida && interactive && (
            <p className="mt-1 text-[10px] text-jf-dim">
              Pode alterar esta resposta — o restante do fluxo é refeito.
            </p>
          )}
        </div>
      )}

      {(node.tipo === 'acao' || node.tipo === 'diagnostico') && (
        <div className="mt-2.5 space-y-2">
          {node.wa && (
            <div className="rounded-md border border-jf-border bg-black/20 p-2 text-[11px] text-jf-muted">
              <span className="font-bold text-jf-yellow">{node.wa.label}</span>
              <span className="mt-0.5 block italic">“{node.wa.msg}”</span>
              <span className="mt-0.5 block text-[10px] text-jf-dim">
                Contato resolvido pelos Contatos de Emergência (banco).
              </span>
            </div>
          )}
          <label
            className={`flex items-center gap-2.5 rounded-lg bg-white/[0.03] px-3 py-2 ${
              canInteract ? 'cursor-pointer' : 'cursor-default'
            }`}
          >
            <input
              type="checkbox"
              checked={actionDone}
              disabled={!canInteract}
              onChange={onToggleAction}
              className="h-[18px] w-[18px] accent-jf-yellow"
            />
            <span className="text-xs text-jf-muted">
              {actionDone
                ? !interactive
                  ? 'Ação concluída'
                  : 'Ação concluída — desmarque para refazer'
                : 'Concluí esta ação'}
            </span>
          </label>
        </div>
      )}

      {isTerminal(node) && canInteract && <TerminalHint node={node} />}
    </div>
  );
}

function TerminalHint({ node }: { node: FlowNode }) {
  if (node.tipo === 'paebm') {
    return (
      <div className="mt-2 rounded-lg border border-level-emergencia/50 bg-level-emergencia/10 p-2.5 text-xs font-bold text-red-200">
        🆘 Emergência — acione as sirenes e o Protocolo PAEBM. Use o encaminhamento abaixo.
      </div>
    );
  }
  if (node.tipo === 'baixa') {
    return (
      <div className="mt-2 rounded-lg border border-sky-500/40 bg-sky-500/10 p-2.5 text-xs font-bold text-sky-200">
        → Fim do fluxo do vigilante. Encaminhe ao RT para a baixa (abaixo).
      </div>
    );
  }
  // GUPM-190 T1: 'fim' de rotina (nada anormal) — não há TARP a registrar.
  if (node.semRegistro) {
    return (
      <div className="mt-2 rounded-lg border border-jf-border bg-jf-surface2/60 p-2.5 text-xs font-bold text-jf-muted">
        ✓ Rotina — nenhuma anomalia a registrar. Descarte o rascunho ou cancele.
      </div>
    );
  }
  return (
    <div className="mt-2 rounded-lg border border-level-normal/40 bg-level-normal/10 p-2.5 text-xs font-bold text-level-normal">
      ✓ Fluxo concluído. Registre a conclusão abaixo.
    </div>
  );
}
